import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useI18n } from "@/i18n/i18n";
import { GripVertical, MapPin, Layers, Globe, MessageCircle, Phone, Instagram, UserPlus, Users, Handshake } from "lucide-react";

function relativeTime(iso, t) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffH = Math.floor((now - then) / (1000 * 60 * 60));
  if (diffH < 1) return t("lead_just_now");
  if (diffH < 24) return t("lead_hours_ago").replace("{n}", diffH);
  const diffD = Math.floor(diffH / 24);
  return t("lead_days_ago").replace("{n}", diffD);
}

const SOURCE_ICONS = {
  wordpress_cf7: Globe,
  whatsapp: MessageCircle,
  phone: Phone,
  instagram: Instagram,
  referral: Users,
  manual: UserPlus,
};

const SOURCE_COLORS = {
  wordpress_cf7: "text-slate-500 bg-slate-100",
  whatsapp: "text-emerald-600 bg-emerald-50",
  phone: "text-blue-600 bg-blue-50",
  instagram: "text-pink-600 bg-pink-50",
  referral: "text-violet-600 bg-violet-50",
  manual: "text-amber-600 bg-amber-50",
};

export default function LeadCard({ lead, onClick, isDragging = false }) {
  const { t } = useI18n();

  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging: sortableDragging,
  } = useSortable({ id: lead.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: sortableDragging ? 0.4 : 1,
  };

  const source = lead.source || "wordpress_cf7";
  const SourceIcon = SOURCE_ICONS[source] || Globe;
  const sourceColor = SOURCE_COLORS[source] || SOURCE_COLORS.wordpress_cf7;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300 transition-all cursor-pointer group ${isDragging ? "shadow-lg border-brand-blue" : ""}`}
      data-testid={`lead-card-${lead.id}`}
      onClick={(e) => {
        if (sortableDragging) return;
        onClick && onClick(e);
      }}
    >
      <div className="p-3">
        <div className="flex items-start gap-2">
          <button
            {...attributes}
            {...listeners}
            className="mt-0.5 text-slate-300 hover:text-slate-500 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => e.stopPropagation()}
            aria-label="Drag handle"
            data-testid={`lead-drag-${lead.id}`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <div className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${sourceColor}`} title={t(`lead_source_${source}`)}>
                <SourceIcon className="h-3 w-3" />
              </div>
              <div className="font-medium text-sm text-slate-900 truncate">
                {[lead.name, lead.last_name].filter(Boolean).join(" ") || lead.email || lead.whatsapp}
              </div>
              {lead.affiliate_code && (
                <span
                  className="inline-flex items-center gap-0.5 flex-shrink-0 px-1.5 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue text-[10px] font-semibold uppercase tracking-wide border border-brand-blue/20"
                  title={`Referido por afiliado · ${lead.affiliate_code}`}
                  data-testid={`lead-card-affiliate-badge-${lead.id}`}
                >
                  <Handshake className="h-3 w-3" />
                  <span className="hidden sm:inline">{lead.affiliate_code}</span>
                </span>
              )}
            </div>
          </div>
        </div>
        {(lead.campus || lead.group) && (
          <div className="mt-2 space-y-1">
            {lead.campus && (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
                <MapPin className="h-3 w-3" />
                <span className="truncate">{t(`lead_campus_${lead.campus}`)}</span>
              </div>
            )}
            {lead.group && (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-600">
                <Layers className="h-3 w-3 flex-shrink-0" />
                <span className="truncate" title={lead.group}>{lead.group}</span>
              </div>
            )}
          </div>
        )}
        <div className="mt-2 text-[10px] text-slate-400">{relativeTime(lead.created_at, t)}</div>
      </div>
    </div>
  );
}
